import { useState } from 'react'

// Feature Request Page - 2025-01-XX
// Adds a simple, styled form for users to submit SWG server feature requests
// Submission strategy:
// - If VITE_FEATURE_REQUEST_WEBHOOK is defined, POST JSON there
// - Else, fall back to mailto link composition, and store a copy in localStorage for reference
// This keeps the site functional without a backend while enabling easy backend hookup later

type Priority = 'Low' | 'Medium' | 'High'

export function FeatureRequestPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    title: '',
    description: '',
    category: 'General',
    priority: 'Medium' as Priority,
    consent: true,
  })
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const webhook = import.meta.env.VITE_FEATURE_REQUEST_WEBHOOK as string | undefined

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    // Type guard for checkbox inputs - 2025-01-XX
    if (type === 'checkbox') {
      const input = e.target as HTMLInputElement
      setForm(prev => ({ ...prev, [name]: input.checked }))
    } else {
      setForm(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setMessage(null)

    const payload = {
      ...form,
      submittedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      page: location.pathname,
    }

    // Store a local copy for reference/debugging - 2025-01-XX
    try {
      const existing = JSON.parse(localStorage.getItem('featureRequests') || '[]')
      existing.push(payload)
      localStorage.setItem('featureRequests', JSON.stringify(existing))
    } catch {}

    if (webhook) {
      // Preferred: POST to webhook if provided - 2025-01-XX
      try {
        const res = await fetch(webhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
        setMessage('Feature request submitted successfully. Thank you!')
        setForm({ name: '', email: '', title: '', description: '', category: 'General', priority: 'Medium', consent: true })
      } catch (err) {
        console.error('Webhook submit failed:', err)
        setMessage('Submission failed. Please try again or use the email option.')
      } finally {
        setSubmitting(false)
      }
      return
    }

    // Fallback: compose a mailto link - 2025-01-XX
    try {
      const to = 'swg-origins@server.local'
      const subject = encodeURIComponent(`[Feature Request] ${form.title || 'New Request'}`)
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nPriority: ${form.priority}\nCategory: ${form.category}\n\nDescription:\n${form.description}\n\nSubmitted: ${new Date().toLocaleString()}`
      )
      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`
      setMessage('Opening your email client to send the request...')
    } catch {
      setMessage('Could not open email client. Your request is saved locally.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen pt-20 pb-10 relative">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-starjedi text-cyan-400 tracking-wider mb-2">
            FEATURE REQUESTS
          </h1>
          <p className="text-sm text-gray-300 max-w-2xl mx-auto">
            Share ideas to improve the SWG: Origins server. We review requests regularly and prioritize based on impact.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-900/60 border border-cyan-500/30 rounded-lg p-6 holographic-border">
          {/* Contact Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-mono text-cyan-400 mb-1">NAME</label>
              <input
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                className="w-full px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-cyan-400 mb-1">EMAIL</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="name@example.com"
                className="w-full px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono"
              />
            </div>
          </div>

          {/* Title */}
          <div className="mb-4">
            <label className="block text-xs font-mono text-cyan-400 mb-1">FEATURE TITLE</label>
            <input
              name="title"
              type="text"
              value={form.title}
              onChange={handleChange}
              placeholder="Short, descriptive title"
              required
              className="w-full px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono"
            />
          </div>

          {/* Details Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-mono text-cyan-400 mb-1">CATEGORY</label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 focus:outline-none focus:border-cyan-400 font-mono"
              >
                <option>General</option>
                <option>Combat</option>
                <option>Crafting</option>
                <option>Economy</option>
                <option>Events</option>
                <option>QoL</option>
                <option>UI/UX</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono text-cyan-400 mb-1">PRIORITY</label>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 focus:outline-none focus:border-cyan-400 font-mono"
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-xs font-mono text-cyan-400 mb-1">DESCRIPTION</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Explain the feature, why it's needed, and expected impact."
              required
              rows={6}
              className="w-full px-3 py-2 bg-black/50 border border-cyan-400/40 rounded text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono"
            />
          </div>

          {/* Consent */}
          <div className="mb-4 flex items-center gap-2">
            <input
              id="consent"
              name="consent"
              type="checkbox"
              checked={form.consent}
              onChange={handleChange}
              className="h-4 w-4"
            />
            <label htmlFor="consent" className="text-xs text-gray-300">
              I consent to my request being stored and reviewed.
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 font-mono">
              {webhook ? 'Submitting to server endpoint' : 'Email/local submission (no server configured)'}
            </span>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white rounded font-mono"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>

          {message && (
            <div className="mt-4 text-sm text-cyan-300 font-mono">
              {message}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}


